import { GetProgrammaticCampaignMobileDeliveryBreakdownRO } from "@intentsify/dto";
import { useQuery } from "@tanstack/react-query";
import { Card, MIN_MODAL_CONTENT_HEIGHT, PieChart } from "components";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { useMemo, useState } from "react";
import { DisplayPerformanceFilters } from "../../../../types";
import {
  ChartExport,
  useExportableChart,
} from "../../../../../../../../shared/components";
import { MdAnalytics } from "react-icons/md";
import { isPopulatedArray } from "@intentsify/utils";

type MobileDeliveryBreakdownProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getMobileDeliveryBreakdown: (
    id: string,
    filters: DisplayPerformanceFilters,
  ) => Promise<GetProgrammaticCampaignMobileDeliveryBreakdownRO>;
};

const TITLE = "Mobile Delivery Breakdown";

export const MobileDeliveryBreakdown = (
  props: MobileDeliveryBreakdownProps,
) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <MobileDeliveryBreakdownContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const MobileDeliveryBreakdownContent = ({
  campaignId,
  filters,
  getMobileDeliveryBreakdown,
}: MobileDeliveryBreakdownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: rawData, isFetching } = useQuery({
    queryKey: ["getMobileDeliveryBreakdown", campaignId, filters],
    queryFn: async () => await getMobileDeliveryBreakdown(campaignId, filters),
  });

  const data = useMemo(() => {
    if (isFetching || !rawData?.mobileDeliveryBreakdown) {
      return [];
    }

    return rawData.mobileDeliveryBreakdown.map((platform) => ({
      label: platform.platformName,
      value: platform.impressions,
    }));
  }, [rawData, isFetching]);

  const chart = useExportableChart({
    title: TITLE,
    campaignId,
    chart: (
      <PieChart
        minHeight={isExpanded ? MIN_MODAL_CONTENT_HEIGHT : undefined}
        stretch
        legendPosition="bottom"
        data={data ?? []}
        displayAsPercent={true}
      />
    ),
  });

  return (
    <Card
      onExpandedChange={setIsExpanded}
      isLoading={isFetching}
      title={chart.title}
      hasData={!isFetching && isPopulatedArray(data)}
      noDataMessage="Currently there is no data available for the selected filters or visuals."
      noDataIcon={MdAnalytics}
      isExpandable
      actions={
        <ChartExport
          size="small"
          campaignId={campaignId}
          title={chart.title}
          onExportPNG={chart.downloadAsPng}
          data={data}
        />
      }
    >
      {chart.component}
      {chart.exportComponent}
    </Card>
  );
};
